/**
 * @file    NumericSettingsManager.tsx
 * @scope   VIEW
 * @summary Advanced-settings panel for Percentage/Frequency on numeric fields: an operator selector
 *          (<, <=, >, >=, =, Between, Outside) and one or two number inputs.
 * @consumes FieldsManager models, numericSettingsUtils
 * @produces `NumericSettings` component
 * @sideEffects None — controlled inputs; changes bubble up via `onFieldChange`.
 */
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { NumericValueOperator } from 'models/Enterprise/FieldsManagerModel';
import { CalcSettingsPanelProps } from './PercentageSettingsManager';
import {
  NUMERIC_OPERATOR_OPTIONS,
  buildDefaultNumericSettings,
  getRequiredValueCount,
  isRangeOperator,
} from '../../utils/views/partitionsManager/calculationsManager/numericSettingsUtils';
import { isPercentageSettingsIncomplete } from '../../utils/validation/fieldValidation';

const toInputs = (values: number[]): string[] => [
  values[0] != null ? String(values[0]) : '',
  values[1] != null ? String(values[1]) : '',
];

const NumericSettings: React.FC<CalcSettingsPanelProps> = ({ selectedField, onFieldChange }) => {
  const fieldGuid = selectedField.field ? selectedField.field.fieldGuid : undefined;
  const settings = selectedField.numericSettings ?? buildDefaultNumericSettings();

  // Raw strings so partially typed numbers (e.g. "-" or "1.") aren't lost between renders
  const [inputs, setInputs] = useState<string[]>(toInputs(settings.values));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setInputs(toInputs(selectedField.numericSettings?.values ?? []));
    setTouched(false);
  }, [fieldGuid]);

  const commit = (valueOperator: NumericValueOperator, nextInputs: string[]) => {
    const values = nextInputs
      .slice(0, getRequiredValueCount(valueOperator))
      .filter((s) => s.trim() !== '' && Number.isFinite(Number(s)))
      .map(Number);
    onFieldChange?.({ ...selectedField, numericSettings: { valueOperator, values } });
  };

  const handleOperatorChange = (option: { value: NumericValueOperator } | null) => {
    if (!option) return;
    commit(option.value, inputs);
  };

  const handleInputChange = (position: number, raw: string) => {
    const nextInputs = [...inputs];
    nextInputs[position] = raw;
    setInputs(nextInputs);
    commit(settings.valueOperator, nextInputs);
  };

  const isRange = isRangeOperator(settings.valueOperator);
  const showValidation = touched && isPercentageSettingsIncomplete(selectedField);
  const isRangeOrderInvalid =
    isRange && settings.values.length === 2 && settings.values[0] >= settings.values[1];

  const renderNumberInput = (position: number, label: string) => (
    <div
      className={`diy-input-container${showValidation ? ' field-input--invalid' : ''}`}
      key={position}
    >
      <div className="diy-material-textfield">
        <input
          className="diy-input"
          type="number"
          aria-label={label}
          value={inputs[position]}
          onChange={(e) => handleInputChange(position, e.target.value)}
          onBlur={() => setTouched(true)}
        />
        <label className="diy-label">{label}</label>
      </div>
    </div>
  );

  return (
    <div className="numeric-settings-container">
      <div className="diy-input-container">
        <div className="diy-material-textfield">
          <Select
            className="diy-select"
            options={NUMERIC_OPERATOR_OPTIONS}
            value={NUMERIC_OPERATOR_OPTIONS.find((o) => o.value === settings.valueOperator)}
            onChange={handleOperatorChange}
            isClearable={false}
            styles={{ control: (s: any) => ({ ...s, maxWidth: '100%' }) }}
          />
          <label className="diy-label select">Operator</label>
        </div>
      </div>
      {isRange
        ? [renderNumberInput(0, 'From'), renderNumberInput(1, 'To')]
        : renderNumberInput(0, 'Value')}
      {showValidation && (
        <div className="diy-required-field-wrapper">
          <span className="diy-required-field">
            {isRangeOrderInvalid ? '"From" must be less than "To"' : 'You must enter a value'}
          </span>
        </div>
      )}
    </div>
  );
};

export default NumericSettings;
